import axiosClient from '@/api/axios/axiosClient';
import { useMutation as useReactMutation } from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api';
import { LoginResponseSchema, type LoginResponse } from '@/types/api/response/auth';
import { RegisterPayloadSchema, type RegisterPayload } from '@/types/api/payload/auth';
import useAppStore from '@/stores/appStore';

export const authKeys = {
  all: ['auth'] as const,
  login: () => [...authKeys.all, 'login'] as const,
  logout: () => [...authKeys.all, 'logout'] as const,
};

export type LoginVariables = { accessToken: string };

export async function loginWithAccessToken(
  accessToken: string,
): Promise<ApiResponse<LoginResponse> | null> {
  const res = await axiosClient.post<ApiResponse<LoginResponse>>(
    '/login',
    undefined,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  
  return res.status === 204 ? null : res.data;
}

export function useLogin({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ApiResponse<LoginResponse> | null) => void;
  onError?: (error: unknown, variables: LoginVariables, context: unknown) => void;
} = {}) {
  return useReactMutation<ApiResponse<LoginResponse> | null, unknown, LoginVariables>({
    mutationKey: authKeys.login(),
    mutationFn: async ({ accessToken }) => {
      const data = await loginWithAccessToken(accessToken);
      
      // Runtime validation of backend response
      if (data !== null) {
        const validatedResponse = LoginResponseSchema.parse(data.data);
        
        // The login response identifies the account; profile details are fetched separately.
        useAppStore.getState().setUser({
          id: validatedResponse.id,
          username: validatedResponse.username,
        });
        useAppStore.getState().setBackendUserId(validatedResponse.id);
        useAppStore.getState().setRegistrationRequired(false);
      }
      
      return data;
    },
    onSuccess,
    onError,
  });
}

// ----- Logout (backend) -----

export async function logoutFromApi(accessToken?: string): Promise<ApiResponse<unknown>> {
  const res = await axiosClient.post<ApiResponse<unknown>>(
    '/logout',
    undefined,
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined,
  );
  return res.data;
}

export function useLogout({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ApiResponse<unknown>) => void;
  onError?: (error: unknown) => void;
} = {}) {
  return useReactMutation<ApiResponse<unknown>, unknown, { accessToken?: string }>({
    mutationKey: authKeys.logout(),
    mutationFn: async ({ accessToken }) => {
      // Only notify backend — do NOT clear store here.
      // Auth0's logout redirect will unload the page.
      // Clearing store prematurely causes RequireAuth to trigger login redirect
      // before the Auth0 redirect can complete.
      return await logoutFromApi(accessToken);
    },
    onSuccess,
    onError,
  });
}

export async function registerAccount(payload: RegisterPayload): Promise<void> {
  const response = await axiosClient.post<ApiResponse<LoginResponse>>(
    '/register',
    RegisterPayloadSchema.parse(payload),
  );
  const account = LoginResponseSchema.parse(response.data?.data);
  useAppStore.getState().setUser({ id: account.id, username: account.username });
  useAppStore.getState().setBackendUserId(account.id);
  useAppStore.getState().setRegistrationRequired(false);
}
